import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { Package, Tag, TrendingUp, ArrowRight, Calendar, FileText, Clock, CheckCircle, Truck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import DashboardWrapper from '../../components/DashboardWrapper';
import api from '../../lib/api';
import resolveProduct from '../../domain/product/resolveProduct';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800', icon: Clock },
  confirmed: { label: 'Confirmée', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  shipped: { label: 'Expédiée', color: 'bg-indigo-100 text-indigo-800', icon: Truck },
  delivered: { label: 'Livrée', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
};

const fmtPrice = (v) => v != null ? `${Number(v).toFixed(2).replace('.', ',')} €` : '—';
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }); }
  catch { return iso.slice(0, 10); }
};

/**
 * PartnerDashboard V3 - Tableau de bord partenaire unifié + commandes
 */
const PartnerDashboard = () => {
  const { user, activeOrg } = useAuth();
  // Phase 4.5b — scope par l'organisation active
  const partnerId = activeOrg?.org_id;
  const [stats, setStats] = useState({
    totalProducts: 0,
    precommandes: 0,
    nouveautes: 0,
    offresActives: 0
  });
  const [orderStats, setOrderStats] = useState(null);
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentOffers, setRecentOffers] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    if (partnerId) {
      loadDashboardData(controller.signal);
    } else {
      setStats({ totalProducts: 0, precommandes: 0, nouveautes: 0, offresActives: 0 });
      setOrderStats(null);
      setRecentProducts([]);
      setRecentOffers([]);
      setRecentOrders([]);
      setLoading(false);
    }
    return () => controller.abort();
  }, [partnerId]);

  const loadDashboardData = async (signal) => {
    try {
      const [productsRes, offersRes2, ordersStatsRes, ordersRes] = await Promise.allSettled([
        api.get(`/products?partner_id=${partnerId}`, { signal }),
        api.get(`/offers?partner_id=${partnerId}`, { signal }),
        api.get(`/partner/purchase-orders/stats?org_id=${partnerId}`, { signal }),
        api.get(`/partner/purchase-orders?org_id=${partnerId}`, { signal }),
      ]);

      const rawProd = productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : [];
      const products = Array.isArray(rawProd) ? rawProd : (rawProd?.products || []);
      const offers = offersRes2.status === 'fulfilled' ? (offersRes2.value.data || []) : [];

      setStats({
        totalProducts: products.length,
        precommandes: products.filter(p => p.is_precommande_active).length,
        nouveautes: products.filter(p => p.is_nouveaute || p.is_nouveaute_a_venir).length,
        offresActives: offers.filter(o => o.is_active).length
      });

      if (ordersStatsRes.status === 'fulfilled') {
        setOrderStats(ordersStatsRes.value.data);
      }

      if (ordersRes.status === 'fulfilled') {
        setRecentOrders((ordersRes.value.data || []).slice(0, 5));
      }

      setRecentProducts(products.slice(0, 5));
      setRecentOffers(offers.filter(o => o.is_active).slice(0, 3));
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Produits', value: stats.totalProducts, icon: Package, color: 'text-blue-600', bg: 'bg-blue-50' },
    { title: 'Pré-commandes', value: stats.precommandes, icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
    { title: 'Offres actives', value: stats.offresActives, icon: Tag, color: 'text-amber-600', bg: 'bg-amber-50' },
    { title: 'Commandes en attente', value: orderStats?.pending || 0, icon: FileText, color: 'text-indigo-600', bg: 'bg-indigo-50' }
  ];

  if (loading) {
    return (
      <DashboardWrapper>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <div className="space-y-6" data-testid="partner-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Tableau de Bord</h1>
          <p className="text-muted-foreground mt-1">
            Bienvenue {user.prenom_utilisateur || user.pseudo || 'Partenaire'}
          </p>
        </div>

        {/* Stats Grid - 4 cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <Card key={index} data-testid={`stat-card-${index}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${stat.bg}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-muted-foreground">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Commandes récentes — full width */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Commandes récentes</CardTitle>
            <Link to="/dashboard/partner/commandes">
              <Button variant="ghost" size="sm">
                Voir toutes <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p className="text-muted-foreground">Aucune commande reçue pour le moment</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentOrders.map(po => {
                  const st = STATUS_CONFIG[po.status] || STATUS_CONFIG.pending;
                  const Icon = st.icon;
                  const userSnap = po.snapshot_user || {};
                  return (
                    <Link key={po.id} to={`/dashboard/partner/commandes/${po.id}`}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors"
                      data-testid={`recent-order-${po.order_number}`}>
                      <div className="w-9 h-9 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="h-4 w-4 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">{po.order_number}</span>
                          <Badge className={`text-[10px] ${st.color}`}>
                            <Icon className="h-3 w-3 mr-0.5" />{st.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{userSnap.nom_magasin || userSnap.email || 'Client'} — {fmtDate(po.created_at)}</p>
                      </div>
                      <span className="text-sm font-bold tabular-nums">{fmtPrice(po.snapshot_totals?.total)}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Produits récents */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Produits récents</CardTitle>
              <Link to="/dashboard/partner/produits">
                <Button variant="ghost" size="sm">
                  Voir tous <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentProducts.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-muted-foreground mb-3">Aucun produit</p>
                  <Link to="/dashboard/partner/produits">
                    <Button size="sm">Ajouter un produit</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentProducts.map((product) => (
                    <div key={product.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <img
                        src={product.visuels?.[0] || 'https://images.unsplash.com/photo-1554309129-5137bb0c03dc?w=100'}
                        alt={product.intitule}
                        className="w-12 h-12 object-cover rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{product.intitule}</p>
                        <p className="text-sm text-muted-foreground">
                          {(() => { const rp = resolveProduct(product).pricing; return rp.hasAnyPrice ? `${rp.price.toFixed(2)}€ HT` : rp.priceLabel; })()}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {product.is_precommande_active && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Préco</Badge>
                        )}
                        {product.is_nouveaute && (
                          <Badge variant="outline" className="text-xs bg-green-50 text-green-700">New</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Offres actives */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">Offres actives</CardTitle>
              <Link to="/dashboard/partner/offres">
                <Button variant="ghost" size="sm">
                  Gérer <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentOffers.length === 0 ? (
                <div className="text-center py-8">
                  <Tag className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-muted-foreground mb-3">Aucune offre active</p>
                  <Link to="/dashboard/partner/offres">
                    <Button size="sm">Créer une offre</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentOffers.map((offer) => (
                    <div key={offer.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                      <div className={`p-2 rounded-lg ${
                        offer.type === 'remise' ? 'bg-green-100' :
                        offer.type === 'franco' ? 'bg-blue-100' :
                        offer.type === 'goodies' ? 'bg-purple-100' : 'bg-amber-100'
                      }`}>
                        <Tag className={`h-5 w-5 ${
                          offer.type === 'remise' ? 'text-green-600' :
                          offer.type === 'franco' ? 'text-blue-600' :
                          offer.type === 'goodies' ? 'text-purple-600' : 'text-amber-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{offer.title}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {offer.start_date || 'Permanent'}
                            {offer.end_date && ` → ${offer.end_date}`}
                          </span>
                        </div>
                      </div>
                      {offer.discount_percent && (
                        <Badge className="bg-green-100 text-green-800">-{offer.discount_percent}%</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardWrapper>
  );
};

export default PartnerDashboard;
